import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { Preferences } from "../../profile/objects/preferences";
import { applyColumnResize, ColumnResizePayload } from '../../utils/list/utils/columnResize';
import { EMPTY_PURCHASE, EMPTY_PURCHASE_LIST_CONFIGURATION, EMPTY_PURCHASE_LIST_CRITERIA } from "../constant/purchaseConstant";
import { Purchase } from "../objects/purchase";
import { PurchaseListCriteria } from "../objects/purchaseListCriteria";
import { PurchaseState } from "../objects/purchaseState";
import { setPurchasePreference } from "../utils/setPurchasePreference";


const initialState: PurchaseState = {
    purchase_list: [],
    purchase_list_configuration: EMPTY_PURCHASE_LIST_CONFIGURATION,
    purchase_list_loading: false,
    purchase_list_count: 0,
    purchase_list_criteria: EMPTY_PURCHASE_LIST_CRITERIA,
    purchase: EMPTY_PURCHASE
};

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const addPurchaseListConfigurationPreferenceReducer = (state: PurchaseState, action: PayloadAction<Preferences[]>): PurchaseState => ({
    ...state, purchase_list_configuration: { ...state.purchase_list_configuration, preferences: [...state.purchase_list_configuration.preferences, ...action.payload] }
});

const setPurchaseListConfigurationPreferenceReducer = (state: PurchaseState, action: PayloadAction<Preferences[]>): PurchaseState => ({
    ...state, purchase_list_configuration: { ...state.purchase_list_configuration, preferences: action.payload }
});

const setPreferenceReducer = (state: PurchaseState, action: PayloadAction<Preferences[]>): PurchaseState => {
    const { preferences, columns, order_by, order } = setPurchasePreference(action.payload, state.purchase_list_configuration.columns);
    return {
        ...state,
        purchase_list_configuration: { ...state.purchase_list_configuration, preferences, columns },
        purchase_list_criteria: { ...state.purchase_list_criteria, order, order_by }
    };
};

const moveDownColumnReducer = (state: PurchaseState, action: PayloadAction<number>): PurchaseState => {
    const columns = cloneDeep([...state.purchase_list_configuration.columns]);
    for (let i = 0; i < columns.length; i++) {
        columns[i].last_updated = false;
    }
    const column = { ...columns[action.payload + 1] };
    columns[action.payload + 1] = columns[action.payload];
    columns[action.payload + 1].last_updated = true;
    columns[action.payload] = column;
    return { ...state, purchase_list_configuration: { ...state.purchase_list_configuration, columns } };
};


const moveUpColumnReducer = (state: PurchaseState, action: PayloadAction<number>): PurchaseState => {
    const columns = cloneDeep([...state.purchase_list_configuration.columns]);
    for (let i = 0; i < columns.length; i++) {
        columns[i].last_updated = false;
    }
    const column = { ...columns[action.payload - 1] };
    columns[action.payload - 1] = columns[action.payload];
    columns[action.payload - 1].last_updated = true;
    columns[action.payload] = column;
    return { ...state, purchase_list_configuration: { ...state.purchase_list_configuration, columns } };
};

const toggleColumnReducer = (state: PurchaseState, action: PayloadAction<number>): PurchaseState => {
    const columns = cloneDeep([...state.purchase_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, purchase_list_configuration: { ...state.purchase_list_configuration, columns } };
};

const updatePurchaseListReducer = (state: PurchaseState, action: PayloadAction<Purchase[]>): PurchaseState => ({
    ...state, purchase_list: action.payload
});
const updatePurchaseListCriteriaReducer = (state: PurchaseState, action: PayloadAction<PurchaseListCriteria>): PurchaseState => ({
    ...state, purchase_list_criteria: action.payload
});
const updatePurchaseReducer = (state: PurchaseState, action: PayloadAction<Purchase>): PurchaseState => ({
    ...state, purchase: action.payload
});
const updatePurchaseListLoadingReducer = (state: PurchaseState, action: PayloadAction<boolean>): PurchaseState => ({
    ...state, purchase_list_loading: action.payload
});
const updatePurchaseListCountReducer = (state: PurchaseState, action: PayloadAction<number>): PurchaseState => ({
    ...state, purchase_list_count: action.payload
});

const resizeColumnReducer = (state: PurchaseState, action: PayloadAction<ColumnResizePayload>): void => {
    state.purchase_list_configuration.columns = applyColumnResize(state.purchase_list_configuration.columns, action.payload);
};

export const purchase = createSlice({
    name: 'purchase',
    initialState,
    reducers: {
        addPurchaseListConfigurationPreference: addPurchaseListConfigurationPreferenceReducer,
        setPurchaseListConfigurationPreference: setPurchaseListConfigurationPreferenceReducer,
        moveDownColumn: moveDownColumnReducer,
        moveUpColumn: moveUpColumnReducer,
        toggleColumn: toggleColumnReducer,
        updatePurchaseList: updatePurchaseListReducer,
        updatePurchaseListCriteria: updatePurchaseListCriteriaReducer,
        updatePurchase: updatePurchaseReducer,
        updatePurchaseListLoading: updatePurchaseListLoadingReducer,
        updatePurchaseListCount: updatePurchaseListCountReducer,
        resizeColumn: resizeColumnReducer
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    addPurchaseListConfigurationPreference,
    setPurchaseListConfigurationPreference,
    moveDownColumn,
    moveUpColumn,
    toggleColumn,
    updatePurchaseList,
    updatePurchaseListCriteria,
    updatePurchase,
    updatePurchaseListLoading,
    updatePurchaseListCount,
    resizeColumn
} = purchase.actions;